import type { ActivityBatchPayload } from '@/analytics/ActivityCollector';
import { getCurrentToken } from '@/api/auth';
import { stwFetch } from '@/utils/stwFetch';

const API_URL = import.meta.env.VITE_API_URL;

export interface ItemEngagementStats {
  id: string;
  name: string;
  createdAt: string;
  interactions: number;
  engagedMs: number;
  avgMsPerVisit: number;
}

export type EntityEngagementSortBy =
  | 'created'
  | 'interactions'
  | 'engagedMs'
  | 'avgMsPerVisit';

/** "Results from" filter, shared by every engagement/activity-record query below. */
export type DateRangeFilter = '24h' | '7d' | '30d' | '1y' | 'all';

export const DATE_RANGE_OPTIONS: { value: DateRangeFilter; label: string }[] = [
  { value: '24h', label: 'Past 24 hours' },
  { value: '7d', label: 'Past week' },
  { value: '30d', label: 'Past month' },
  { value: '1y', label: 'Past year' },
  { value: 'all', label: 'All time' },
];

export interface EntityEngagementQuery {
  page?: number;
  limit?: number;
  sortBy?: EntityEngagementSortBy;
  sortOrder?: 'asc' | 'desc';
  range?: DateRangeFilter;
}

export interface PaginatedEngagementResult {
  items: ItemEngagementStats[];
  total: number;
  page: number;
  limit: number;
}

const entityEngagementParams = (query: EntityEngagementQuery) => {
  const params = new URLSearchParams();
  if (query.page) params.append('page', query.page.toString());
  if (query.limit) params.append('limit', query.limit.toString());
  if (query.sortBy) params.append('sortBy', query.sortBy);
  if (query.sortOrder) params.append('sortOrder', query.sortOrder);
  if (query.range) params.append('range', query.range);
  return params;
};

/** Paginated item-level engagement stats (interactions, engaged time, avg. time
 * per visit) for the current user's own items. */
export const getItemEngagementStats = async (
  query: EntityEngagementQuery = {}
): Promise<PaginatedEngagementResult> =>
  stwFetch('GET', `/activity/dashboard/items?${entityEngagementParams(query)}`);

/** Admin-scoped equivalent of `getItemEngagementStats()`, for one target user. */
export const getUserItemsEngagement = async (
  userId: string,
  query: EntityEngagementQuery = {}
): Promise<PaginatedEngagementResult> =>
  stwFetch(
    'GET',
    `/activity/dashboard/users/${userId}/items?${entityEngagementParams(query)}`
  );

export interface PaginatedSceneEngagementResult {
  scenes: ItemEngagementStats[];
  total: number;
  page: number;
  limit: number;
}

/** Paginated scene-level engagement stats for the current user's own scenes -
 * scene equivalent of `getItemEngagementStats()`. */
export const getSceneEngagementStats = async (
  query: EntityEngagementQuery = {}
): Promise<PaginatedSceneEngagementResult> =>
  stwFetch(
    'GET',
    `/activity/dashboard/scenes?${entityEngagementParams(query)}`
  );

/** Admin-scoped scene engagement list, same shape as `getUserItemsEngagement()`. */
export const getUserScenesEngagement = async (
  userId: string,
  query: EntityEngagementQuery = {}
): Promise<PaginatedSceneEngagementResult> =>
  stwFetch(
    'GET',
    `/activity/dashboard/users/${userId}/scenes?${entityEngagementParams(query)}`
  );

export interface UserActivityListRow {
  id: string;
  username: string;
  createdAt: string;
}

export interface UserActivityListQuery {
  search?: string;
  sortBy?: 'name' | 'dateJoined';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

/** Bare, paginated user list for the admin-only user-activity dashboard - no
 * per-row summary stat, that's `getUserActivitySummary()` below. */
export const getUsersForActivityDashboard = async (
  query: UserActivityListQuery = {}
): Promise<{
  users: UserActivityListRow[];
  total: number;
  page: number;
  limit: number;
  /** Earliest content-modifying event ever recorded on this deployment. */
  trackingStartDate: string;
}> => {
  const params = new URLSearchParams();
  if (query.search) params.append('search', query.search);
  if (query.sortBy) params.append('sortBy', query.sortBy);
  if (query.sortOrder) params.append('sortOrder', query.sortOrder);
  if (query.page) params.append('page', query.page.toString());
  if (query.limit) params.append('limit', query.limit.toString());
  return stwFetch('GET', `/activity/dashboard/users?${params}`);
};

export interface UserActivitySummary {
  engagedMs: number;
  itemsCreated: number;
  scenesCreated: number;
  annotationsCreated: number;
  cameraViewsCreated: number;
  commentsMade: number;
  annotationsViewed: number;
  cameraViewsSelected: number;
  annotationLinksClicked: number;
}

/** One user's summary-tile metrics for the user-activity detail view. */
export const getUserActivitySummary = (
  userId: string
): Promise<UserActivitySummary> =>
  stwFetch('GET', `/activity/dashboard/users/${userId}/summary`);

export interface UserModifyEvent {
  id: string;
  type: string;
  context: Record<string, unknown>;
  createdAt: string;
  /** Current name of whichever item/scene/layer/annotation/camera view this row is
   * about - `null` if that entity has since been deleted. */
  targetId: string | null;
  targetName: string | null;
}

export interface UserModifyEventsQuery {
  page?: number;
  limit?: number;
  entityId?: string;
  includeNonSuccess?: boolean;
  range?: DateRangeFilter;
}

/** Paginated, newest-first content-modifying event history for one user. */
export const getUserModifyEvents = async (
  userId: string,
  query: UserModifyEventsQuery = {}
): Promise<{
  events: UserModifyEvent[];
  total: number;
  page: number;
  limit: number;
  /** Earliest content-modifying event ever recorded on this deployment -
   * nothing before this date was tracked. */
  trackingStartDate: string;
}> => {
  const params = new URLSearchParams();
  if (query.page) params.append('page', query.page.toString());
  if (query.limit) params.append('limit', query.limit.toString());
  if (query.entityId) params.append('entityId', query.entityId);
  if (query.includeNonSuccess) params.append('includeNonSuccess', 'true');
  if (query.range) params.append('range', query.range);
  return stwFetch(
    'GET',
    `/activity/dashboard/users/${userId}/modify-events?${params}`
  );
};

export interface AnnotationLinkEngagementStats {
  url: string;
  clicks: number;
}

export interface AnnotationEngagementStats {
  id: string;
  name: string;
  opens: number;
  openRate: number;
  links: AnnotationLinkEngagementStats[];
}

export interface LayerEngagementStats {
  id: string;
  name: string;
  annotations: AnnotationEngagementStats[];
}

export interface AnnotationEngagementReport {
  visits: number;
  layers: LayerEngagementStats[];
}

const rangeParams = (range?: DateRangeFilter) => {
  const params = new URLSearchParams();
  if (range) params.append('range', range);
  return params;
};

export interface ItemEngagementDetailQuery {
  range?: DateRangeFilter;
  /** Whether to count engagement that happened while this item was inspected
   * from inside a scene - defaults to included, matching today's behavior. */
  includeInSceneInspection?: boolean;
}

const itemEngagementDetailParams = (query: ItemEngagementDetailQuery) => {
  const params = rangeParams(query.range);
  if (query.includeInSceneInspection === false) {
    params.append('includeInSceneInspection', 'false');
  }
  return params;
};

/** Scene equivalent of `getItemEngagementDetail` - no in-scene-inspection toggle
 * here, since a scene's own annotation events never carry that flag. */
export const getSceneAnnotationEngagementStats = (
  sceneId: string,
  range?: DateRangeFilter
): Promise<AnnotationEngagementReport> =>
  stwFetch(
    'GET',
    `/activity/dashboard/scenes/${sceneId}/annotations?${rangeParams(range)}`
  );

export interface CameraViewEngagementStats {
  id: string;
  name: string;
  opens: number;
}

export interface CameraViewLayerEngagementStats {
  id: string;
  name: string;
  cameraViews: CameraViewEngagementStats[];
}

export interface CameraViewEngagementReport {
  layers: CameraViewLayerEngagementStats[];
}

export interface ItemEngagementDetailLayer {
  id: string;
  name: string;
  annotations: AnnotationEngagementStats[];
  cameraViews: CameraViewEngagementStats[];
}

export interface ItemEngagementDetail {
  visits: number;
  layers: ItemEngagementDetailLayer[];
}

/** Combined annotation (open rate, per-link click count) + camera-view (select
 * count) engagement stats for one item, grouped by its real layers (User
 * Comments/default skipped) - one request backing both drill-down panels shown
 * together for a selected item. */
export const getItemEngagementDetail = (
  itemId: string,
  query: ItemEngagementDetailQuery = {}
): Promise<ItemEngagementDetail> =>
  stwFetch(
    'GET',
    `/activity/dashboard/items/${itemId}/engagement?${itemEngagementDetailParams(query)}`
  );

export interface SceneItemInspectionStats {
  objectId: string;
  objectName: string | null;
  itemId: string;
  itemName: string | null;
  inspects: number;
}

export interface SceneItemInspectionReport {
  objects: SceneItemInspectionStats[];
}

/** Inspect counts for one scene's placed items, one row per placed instance
 * (not per item) - see the backend route's own comment for why. */
export const getSceneItemInspectionStats = (
  sceneId: string,
  range?: DateRangeFilter
): Promise<SceneItemInspectionReport> =>
  stwFetch(
    'GET',
    `/activity/dashboard/scenes/${sceneId}/item-inspections?${rangeParams(range)}`
  );

/** Fire-and-forget batch send. Uses `fetch(..., keepalive: true)` rather than
 * `sendBeacon()`, since sendBeacon can't set the `Authorization` header this needs. */
export const sendActivityBatch = async (
  payload: ActivityBatchPayload
): Promise<void> => {
  const token = await getCurrentToken();

  if (import.meta.env.DEV)
    console.debug('[ActivityCollector] sending batch', payload);

  try {
    const response = await fetch(`${API_URL}/activity/batch`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    });
    if (import.meta.env.DEV)
      console.debug('[ActivityCollector] batch sent', response.status);
  } catch (error) {
    // Best-effort: dropped batches are expected (backgrounded tab, network
    // blip, keepalive's queued-bytes cap) and shouldn't surface to the user.
    if (import.meta.env.DEV)
      console.debug('[ActivityCollector] batch send failed', error);
  }
};
